import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getDistribution, getTimeSeries } from './request/request';
import { DistributionItem, TimeSeriesPoint, algoColor } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import FreshnessBar from './FreshnessBar';

interface Props {
    selectedSource: string;
    timeRange: string;
}

const TimeSeriesChart: React.FC<{ points: TimeSeriesPoint[] }> = ({ points }) => {
    const { chartData, locatorNames } = useMemo(() => {
        const names = new Set<string>();
        const data = points.map((pt) => {
            const row: Record<string, string | number> = {
                time: new Date(pt.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            };
            for (const s of pt.series) {
                names.add(s.locatorName);
                row[s.locatorName] = s.mbps;
            }
            return row;
        });
        return { chartData: data, locatorNames: Array.from(names).sort((a, b) => a.localeCompare(b)) };
    }, [points]);

    if (chartData.length === 0) return null;

    return (
        <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis
                    tick={{ fontSize: 10 }}
                    width={54}
                    label={{ value: 'Mbps', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10 } }}
                />
                <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(value: number) => [`${value.toFixed(2)} Mbps`]}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {locatorNames.map((name) => (
                    <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={algoColor(name)}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

const Srv6DashboardTab: React.FC<Props> = ({ selectedSource, timeRange }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [distribution, setDistribution] = useState<DistributionItem[]>([]);
    const [totalBps, setTotalBps] = useState(0);
    const [timeSeries, setTimeSeries] = useState<TimeSeriesPoint[]>([]);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const load = useCallback(async () => {
        if (!selectedSource) return;
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const [distRes, tsRes] = await Promise.all([
                getDistribution(token, selectedSource, timeRange),
                getTimeSeries(token, selectedSource, timeRange, '1m'),
            ]);
            if (distRes.data?.data) {
                setDistribution(distRes.data.data.distribution || []);
                setTotalBps(distRes.data.data.total_bps || 0);
            }
            if (tsRes.data?.data) {
                setTimeSeries(tsRes.data.data.points || []);
            }
            setLastFetched(new Date());
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, selectedSource, timeRange]);

    useEffect(() => { load(); }, [load]);

    const maxDistBps = distribution.length > 0 ? Math.max(...distribution.map((d) => d.bps)) : 1;

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;

    return (
        <div className="flex flex-col gap-6 overflow-auto">
            <FreshnessBar lastFetched={lastFetched} onRefresh={load} />
            <div className="p-4 border rounded-xl border-semantic-border-secondary bg-semantic-bg-primary">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-semantic-text-primary">
                        Flex-Algo Distribution
                    </h2>
                    <span className="text-xs text-semantic-text-secondary">
                        Total: {(totalBps * 8 / 1e6).toFixed(1)} Mbps
                    </span>
                </div>
                {distribution.length === 0 ? (
                    <p className="text-xs text-semantic-text-secondary">No data for selected source</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {distribution.map((d) => (
                            <div key={d.locatorName} className="flex items-center gap-3">
                                <span className="w-28 text-xs font-mono text-semantic-text-primary truncate" title={d.locatorName}>
                                    {d.locatorName}
                                </span>
                                <div className="flex-1 h-5 bg-semantic-bg-secondary rounded overflow-hidden">
                                    <div
                                        className="h-full rounded transition-all duration-300"
                                        style={{
                                            width: `${(d.bps / maxDistBps) * 100}%`,
                                            backgroundColor: algoColor(d.locatorName),
                                        }}
                                    />
                                </div>
                                <span className="w-14 text-xs text-right font-medium text-semantic-text-primary">
                                    {d.pct}%
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="p-4 border rounded-xl border-semantic-border-secondary bg-semantic-bg-primary">
                <h2 className="text-sm font-semibold text-semantic-text-primary mb-3">
                    Traffic by Locator (Mbps)
                </h2>
                {timeSeries.length === 0 ? (
                    <p className="text-xs text-semantic-text-secondary">No time series data</p>
                ) : (
                    <TimeSeriesChart points={timeSeries} />
                )}
            </div>
        </div>
    );
};

export default Srv6DashboardTab;
